/**
 * retryAsync — run an async function, retrying on failure with exponential
 * backoff.
 *
 * CORE-276: home-page master fetches (`getPublicMasterProfiles`) could fail
 * on a single transient error and never recover — the floating-masters hero
 * went permanently empty. In the RU region the edge returns gateway 504s
 * under burst load (e.g. a hard refresh), and the global api-ru fallback
 * deliberately does NOT retry 5xx ("don't burn the budget on backend
 * errors", apiFallback.ts), so those reach the caller as thrown errors.
 * A few backoff retries ride out the transient window and let the content
 * load instead of disappearing.
 *
 * Defaults: 4 retries, 500 ms base → waits ~0.5s, 1s, 2s, 4s between tries
 * (≈7.5 s total worst case). `shouldRetry` lets callers skip retries on
 * errors that won't get better (default: retry everything).
 */
export async function retryAsync<T>(
  fn: () => Promise<T>,
  opts: { retries?: number; baseDelayMs?: number; shouldRetry?: (err: unknown) => boolean } = {},
): Promise<T> {
  const { retries = 4, baseDelayMs = 500, shouldRetry = () => true } = opts;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !shouldRetry(err)) break;
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** attempt));
    }
  }
  throw lastErr;
}
